/**
 * Equipment categories for cars (Utrustningslista).
 * Order of categories and items defines display priority in CarDetails:
 * if a vehicle has equipment from this list, those items are shown first in this order.
 */

export const CAR_EQUIPMENT_CATEGORIES = {
  "🚘 Förarstöd & Assistans (ADAS)": [
    "Farthållare",
    "Adaptiv farthållare (ACC)",
    "Köassistent",
    "Körfilsassistans",
    "Körfilscentrering",
    "Filbytesassistent",
    "Autobroms (AEB)",
    "Fotgängardetektion",
    "Döda vinkel-varning (BLIS)",
    "Rear Traffic Alert",
    "Skyltigenkänning",
    "Trötthetsvarnare",
    "Helljusassistans",
    "Automatisk parkering",
    "Parkeringssensorer fram",
    "Parkeringssensorer bak",
    "Backkamera",
    "360° kamera",
    "Night Vision",
    "Trailer Assist",
    "Remote Park Assist",
    "Autopilot",
  ],
  "⚡ Elbil & Hybrid": [
    "Elbil",
    "Laddhybrid",
    "Snabbladdning (DC)",
    "AC-laddning",
    "Laddport CCS",
    "Laddport Type 2",
    "Värmepump",
    "Batteriförvärmning",
    "Förvärmning via app",
    "Regenerativ bromsning",
    "Enpedalskörning",
    "Räckviddsassistent",
    "Vehicle-to-Load (V2L)",
    "Over-the-Air uppdateringar (OTA)",
  ],
  "🌡 Klimat & Värme": [
    "AC",
    "2-zons klimatanläggning",
    "3-zons klimatanläggning",
    "4-zons klimatanläggning",
    "Parkeringsvärmare",
    "Bränsledriven värmare",
    "Fjärrstyrd värmare",
    "Appstyrd värmare",
    "Motorvärmare",
    "Kupévärmare",
    "Sätesvärme fram",
    "Sätesvärme bak",
    "Ventilerade säten",
    "Rattvärme",
    "Eluppvärmd vindruta",
    "Uppvärmda spolarmunstycken",
  ],
  "🪑 Säten & Interiör": [
    "Elstol förare",
    "Elstol förare med minne",
    "Elstol passagerare",
    "Massagestolar",
    "Sportstolar",
    "Svankstöd",
    "Fällbara baksäten",
    "Elfällbara baksäten",
    "7-sits",
    "6-sits",
    "Tygklädsel",
    "Halvskinn",
    "Helskinn",
    "Alcantara",
    "Nappaläder",
    "Soft close doors",
  ],
  "🖥 Infotainment & Uppkoppling": [
    "Touchskärm",
    "Digital cockpit",
    "Digital förardisplay",
    "Head-Up Display",
    "Navigation",
    "Apple CarPlay /Android Auto",
    "Bluetooth",
    "Digitalradio (DAB)",
    "Röststyrning",
    "WiFi-hotspot",
    "Trådlös telefonladdare",
    "USB",
    "USB-C",
    "230V-uttag",
    "Rear Seat Entertainment",
    "Augmented Reality Navigation",
  ],
  "🔊 Premium ljudsystem": [
    "Bang & Olufsen",
    "Bowers & Wilkins",
    "Harman Kardon",
    "Mark Levinson",
    "Burmester",
    "BOSE",
    "Meridian",
    "Sonos",
    "Rockford Fosgate",
  ],
  "💡 Belysning": [
    "LED-strålkastare",
    "Matrix LED",
    "Pixel LED",
    "Laserljus",
    "Xenon",
    "Kurvljus",
    "LED-varselljus",
    "Ambient lighting",
    "Upplysta dörrhandtag",
  ],
  "🚙 Exteriör": [
    "Taklucka",
    "Panoramaglastak",
    "Öppningsbart panoramatak",
    "Tonade rutor",
    "Solgardiner",
    "Takrails",
    "Dragkrok",
    "Avtagbar dragkrok",
    "Elektrisk dragkrok",
    "Elbaklucka",
    "Handsfree baklucka",
    "Elinfällbara sidospeglar",
    "Avbländande speglar",
    "Lättmetallfälgar",
    "Elektriska el hissar",
    "Keyless Go",
    "Keyless Start",
  ],
  "🛞 Drivlina & Körning": [
    "Paddelväxlar",
    "Fyrhjulsdrift (AWD)",
    "4MOTION",
    "Quattro",
    "xDrive",
    "4Matic",
    "Sportchassi",
    "Adaptiv fjädring",
    "Luftfjädring",
    "Körlägesväljare",
    "Launch Control",
  ],
  "📦 Praktiskt": [
    "Bagagerumsmatta",
    "Insynsskydd",
    "Gummimattor",
    "Textilmattor",
  ],
} as const;

/** Flattened list of equipment keys in category order (fallback for non-highlights). */
const EQUIPMENT_CATEGORY_ORDER: string[] = Object.values(
  CAR_EQUIPMENT_CATEGORIES
).flat();

/**
 * Höjdpunkter (visas först i annons) – exact display order for equipment in listing.
 * These items are shown first when the vehicle has them.
 */
export const EQUIPMENT_HIGHLIGHTS_ORDER: string[] = [
  "Panoramaglastak",
  "Backkamera",
  "360° kamera",
  "Navigation",
  "Parkeringssensorer",
  "Parkeringssensorer fram",
  "Parkeringssensorer bak",
  "Adaptiv farthållare (ACC)",
  "Ventilerade säten",
  "Rattvärme",
  "Elstol förare med minne",
  "Head-Up Display",
  "Apple CarPlay /Android Auto",
  "Digital cockpit",
  "Luftfjädring",
  "Sportstolar",
  "Nappaläder",
  "Matrix LED",
  "Laserljus",
  "Dragkrok",
  "Elbaklucka",
  "Värmepump",
  "Autopilot",
  "Köassistent",
];

const HIGHLIGHTS_SET = new Set(EQUIPMENT_HIGHLIGHTS_ORDER);
const CATEGORY_ORDER_INDEX = new Map(
  EQUIPMENT_CATEGORY_ORDER.map((k, i) => [k, i])
);

/**
 * Sort equipment so Höjdpunkter (highlights) appear first in ad order, then rest by category order.
 */
export function sortEquipmentWithPriority(equipment: string[]): string[] {
  const highlights: string[] = [];
  const inCategory: string[] = [];
  const rest: string[] = [];
  for (const item of equipment) {
    if (HIGHLIGHTS_SET.has(item)) highlights.push(item);
    else if (CATEGORY_ORDER_INDEX.has(item)) inCategory.push(item);
    else rest.push(item);
  }
  highlights.sort(
    (a, b) =>
      EQUIPMENT_HIGHLIGHTS_ORDER.indexOf(a) -
      EQUIPMENT_HIGHLIGHTS_ORDER.indexOf(b)
  );
  inCategory.sort(
    (a, b) =>
      (CATEGORY_ORDER_INDEX.get(a) ?? 0) - (CATEGORY_ORDER_INDEX.get(b) ?? 0)
  );
  return [...highlights, ...inCategory, ...rest];
}
